import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FlowId, FlowViewData, StageGlyph } from '@interfaces/IFlowView';
import { readLastSiteId } from '@utils/lastSite';
import { loadFlowView } from '@/mocks/flowView';
import { PageActions } from '@components/ui/PageFrame/PageFrame';
import { FlowHealthPills } from './panels/FlowHealthPills';
import { FlowSelector } from './panels/FlowSelector';
import { LifecycleStages } from './panels/LifecycleStages';
import { StageDetail } from './panels/StageDetail';
import { ExceptionLayer } from './panels/ExceptionLayer';
import { Ownership } from './panels/Ownership';
import styles from './FlowViewPage.module.css';

/* ---------------------------------------------------------------
   Flow View — Process Intelligence
   1:1 port of the Lovable "Project Command Center → Flow View"
   prototype. Fully mocked; no backend route yet (the Flow domain
   has no D-ORCA collection, DAITEC adapter, or Trusstor primitive
   today). Page composition:

     ┌── Header (title + Flow Health / Overall / Exceptions pills) ─┐
     ├── SELECT FLOW (4 extensible flow tiles)                      │
     ├── MEP Flow — Lifecycle (5 stage cards)                       │
     ├── Stage Detail (Deliver)          │ Exception Layer (3 open) │
     ├── Ownership & Accountability (5 stage owner cards)           │
     └──────────────────────────────────────────────────────────────┘
--------------------------------------------------------------- */

export function FlowViewPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<FlowViewData | null>(null);

  useEffect(() => {
    let alive = true;
    void loadFlowView().then((d) => {
      if (alive) setData(d);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Local override of the mock's default selections. Resolved against
  // the data's defaults at render time so we never have to sync state
  // out of an effect — see react-hooks/set-state-in-effect.
  const [selectedFlowId, setSelectedFlowId] = useState<FlowId | null>(null);
  const [selectedStageKey, setSelectedStageKey] = useState<StageGlyph | null>(null);

  if (!data) return <div aria-busy="true" data-testid="flow-view-loading" />;

  const activeFlowId   = selectedFlowId   ?? data.activeFlowId;
  const activeStageKey = selectedStageKey ?? data.activeStageKey;
  const activeFlow     = data.flows.find((f) => f.id === activeFlowId) ?? data.flows[0]!;
  const stageDetail    = data.stageDetails[activeStageKey];

  // Resolve the Stage Detail action target. Per-site pages get the
  // user's last-known siteId appended (snappier than landing on the
  // SelectSite picker); `/scheduler` is site-agnostic.
  const lastSiteId = readLastSiteId();
  const actionHref =
    stageDetail.actionPath == null
      ? null
      : stageDetail.actionPath === '/scheduler' || !lastSiteId
        ? stageDetail.actionPath
        : `${stageDetail.actionPath}/${lastSiteId}`;

  return (
    <div className={styles.page}>
      <PageActions>
        <FlowHealthPills
          health={data.health}
          labels={{
            flowHealth: t('flowView.health.flow'),
            overall:    t('flowView.health.overall'),
            exceptions: t('flowView.health.exceptions'),
          }}
        />
      </PageActions>
      <FlowSelector
        title={t('flowView.selector.title')}
        meta={t('flowView.selector.meta')}
        countLabel={t('flowView.selector.count', { count: data.flows.length })}
        healthLabel={t('flowView.selector.health')}
        flows={data.flows}
        activeId={activeFlowId}
        onSelect={setSelectedFlowId}
      />

      <LifecycleStages
        title={t('flowView.lifecycle.title', { flow: activeFlow.title })}
        stages={data.stages}
        activeKey={activeStageKey}
        onSelect={setSelectedStageKey}
        statusLabels={{
          complete:    t('flowView.status.complete'),
          'in-progress': t('flowView.status.inProgress'),
          delayed:     t('flowView.status.delayed'),
          pending:     t('flowView.status.pending'),
        }}
      />

      <div className={styles.row}>
        <StageDetail
          eyebrow={t('flowView.stageDetail.eyebrow')}
          detail={stageDetail}
          actionHref={actionHref}
          labels={{
            owner:       t('flowView.stageDetail.owner'),
            due:         t('flowView.stageDetail.due'),
            completion:  t('flowView.stageDetail.completion'),
            dataSource:  t('flowView.stageDetail.dataSource'),
            items:       t('flowView.stageDetail.items'),
            blockers:    t('flowView.stageDetail.blockers'),
          }}
        />
        <ExceptionLayer
          title={t('flowView.exceptions.title')}
          openLabel={t('flowView.exceptions.open', { count: data.exceptions.length })}
          stageLabel={t('flowView.exceptions.stageLabel')}
          exceptions={data.exceptions}
          activeStageKey={activeStageKey}
          onSelect={setSelectedStageKey}
        />
      </div>

      <Ownership
        title={t('flowView.ownership.title')}
        meta={t('flowView.ownership.meta')}
        rows={data.ownership}
      />
    </div>
  );
}
